import React, { useState, useEffect, useRef, useCallback } from "react";
import AdminLayout from "../../components/admin/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import {
  HardDrive, Play, RefreshCw, Trash2, Download, CheckCircle2,
  XCircle, Loader2, ChevronDown, ChevronUp, ExternalLink, Info,
  Unplug, Cloud, Clock, FileArchive, AlertTriangle, FolderOpen
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Status-Badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    success:  { cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", label: "Erfolgreich" },
    error:    { cls: "bg-red-500/10 text-red-400 border-red-500/20", label: "Fehler" },
    running:  { cls: "bg-amber-500/10 text-amber-400 border-amber-500/20", label: "Läuft…" },
  };
  const s = map[status] || { cls: "bg-zinc-700/50 text-zinc-400 border-zinc-600/20", label: status || "–" };
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${s.cls}`}>
      {status === "running" && <Loader2 size={11} className="animate-spin" />}
      {status === "success" && <CheckCircle2 size={11} />}
      {status === "error" && <XCircle size={11} />}
      {s.label}
    </span>
  );
}

// ── Anleitung: Azure App Registration ────────────────────────────────────────
function AzureGuide({ redirectUri }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm text-blue-300 hover:text-blue-200 transition-colors"
        data-testid="azure-guide-toggle"
      >
        <span className="flex items-center gap-2 font-medium">
          <Info size={15} /> Anleitung: Azure App Registration erstellen
        </span>
        {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4 text-sm text-zinc-300 border-t border-blue-500/10">
          <p className="pt-3 text-zinc-400">
            Einmalige Einrichtung in ca. 3 Minuten:
          </p>
          <ol className="space-y-3 list-none">
            {[
              <>
                Öffne <a href="https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade"
                  target="_blank" rel="noreferrer"
                  className="text-blue-400 hover:text-blue-300 underline inline-flex items-center gap-1">
                  Azure Portal → App-Registrierungen <ExternalLink size={11} />
                </a> → <strong className="text-white">„+ Neue Registrierung"</strong>
              </>,
              <>
                <strong className="text-white">Name:</strong> z.B. <code className="bg-zinc-800 px-1 rounded text-xs">HUWND Backup</code>
                <br />
                <strong className="text-white">Kontotypen:</strong>{" "}
                <span className="text-amber-400 font-semibold">„Nur persönliche Microsoft-Konten"</span>
                <br />
                <strong className="text-white">Umleitungs-URI:</strong> Plattform = <span className="text-amber-400 font-semibold">Web</span>,
                URL = <code className="bg-zinc-900 border border-zinc-700 px-2 py-0.5 rounded text-xs text-green-400 select-all break-all">{redirectUri}</code>
              </>,
              <>
                Nach Erstellung: <strong className="text-white">Anwendungs-ID (Client-ID)</strong> oben kopieren
              </>,
              <>
                <strong className="text-white">API-Berechtigungen</strong> → Hinzufügen → Microsoft Graph → Delegiert →{" "}
                <code className="bg-zinc-800 px-1 rounded text-xs">Files.ReadWrite</code>,{" "}
                <code className="bg-zinc-800 px-1 rounded text-xs">offline_access</code>,{" "}
                <code className="bg-zinc-800 px-1 rounded text-xs">User.Read</code>
              </>,
              <>
                <strong className="text-white">Manifest</strong>: <code className="bg-zinc-800 px-1 rounded text-xs">"requestedAccessTokenVersion": 2</code> setzen → Speichern
              </>,
              <>Fertig – Client-ID unten eintragen und <strong className="text-white">„Verbinden"</strong> klicken</>
            ].map((step, i) => (
              <li key={i} className="flex gap-3">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-600/30 text-blue-300 text-xs flex items-center justify-center font-bold mt-0.5">{i + 1}</span>
                <span className="leading-relaxed">{step}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

// ── Setup-Wizard (PKCE) ───────────────────────────────────────────────────────
function SetupWizard({ onConnected, token, prefillClientId = "" }) {
  const [clientId,     setClientId]     = useState(prefillClientId);
  const [clientSecret, setClientSecret] = useState("");
  const [phase,        setPhase]        = useState("idle");
  const [errorMsg,     setErrorMsg]     = useState("");
  const pollRef    = useRef(null);
  const redirectUri = `${window.location.origin}/api/backup/oauth-callback`;

  useEffect(() => { if (prefillClientId) setClientId(prefillClientId); }, [prefillClientId]);

  // Detect OAuth callback result in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("oauth_done")) {
      window.history.replaceState({}, "", window.location.pathname);
      onConnected();
    }
    if (params.get("oauth_error")) {
      const err = decodeURIComponent(params.get("oauth_error"));
      window.history.replaceState({}, "", window.location.pathname);
      setPhase("error");
      setErrorMsg(err);
    }
  }, [onConnected]);

  const startAuth = async () => {
    if (!clientId.trim()) return;
    setPhase("waiting");
    setErrorMsg("");
    try {
      const res = await fetch(`${API}/api/backup/start-pkce-auth`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ client_id: clientId.trim(), client_secret: clientSecret.trim(), redirect_uri: redirectUri }),
      });
      if (!res.ok) {
        let msg = "Fehler";
        try { const d = await res.json(); msg = d.detail || msg; } catch {}
        throw new Error(msg);
      }
      const { auth_url } = await res.json();
      // Open Microsoft login in popup
      const popup = window.open(auth_url, "onedrive_auth",
        "width=520,height=640,toolbar=0,menubar=0,location=0,status=0");
      if (!popup) {
        // Fallback: new tab
        window.open(auth_url, "_blank");
      }
      // Poll status until connected
      pollRef.current = setInterval(async () => {
        try {
          const sr = await fetch(`${API}/api/backup/status`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const sd = await sr.json();
          if (sd.connected) {
            clearInterval(pollRef.current);
            setPhase("idle");
            onConnected(sd.onedrive_user);
          }
        } catch {}
      }, 2000);
    } catch (e) {
      setPhase("error");
      setErrorMsg(e.message);
    }
  };

  useEffect(() => () => clearInterval(pollRef.current), []);

  return (
    <div className="space-y-5">
      <AzureGuide redirectUri={redirectUri} />

      <div className="space-y-3">
        <label className="text-sm text-zinc-300 font-medium">Azure Client-ID</label>
        <input
          data-testid="client-id-input"
          type="text"
          value={clientId}
          onChange={e => setClientId(e.target.value)}
          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          disabled={phase === "waiting"}
          className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-2.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-rose-500/50 font-mono disabled:opacity-50"
        />
      </div>

      <div className="space-y-3">
        <label className="text-sm text-zinc-300 font-medium">
          Client-Secret
          <span className="ml-2 text-xs text-zinc-500 font-normal normal-case">
            (Azure Portal → Zertifikate &amp; Geheimnisse → Neues Geheimnis)
          </span>
        </label>
        <input
          data-testid="client-secret-input"
          type="password"
          value={clientSecret}
          onChange={e => setClientSecret(e.target.value)}
          placeholder="Wert des Client-Geheimnisses"
          disabled={phase === "waiting"}
          className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-2.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-rose-500/50 font-mono disabled:opacity-50"
        />
      </div>

      {phase === "idle" && (
        <button
          data-testid="start-auth-btn"
          onClick={startAuth}
          disabled={!clientId.trim()}
          className="flex items-center gap-2 px-4 py-2.5 bg-rose-600 hover:bg-rose-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors"
        >
          <Cloud size={15} /> Mit OneDrive verbinden
        </button>
      )}

      {phase === "waiting" && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-2">
          <div className="flex items-center gap-2 text-amber-400 font-semibold text-sm">
            <Loader2 size={15} className="animate-spin" />
            Microsoft-Anmeldefenster geöffnet…
          </div>
          <p className="text-zinc-400 text-xs">
            Melde dich mit deinem Microsoft-Konto an und stimme den Berechtigungen zu.
            Diese Seite wird automatisch aktualisiert.
          </p>
          <button
            onClick={() => { clearInterval(pollRef.current); setPhase("idle"); }}
            className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors underline"
          >
            Abbrechen
          </button>
        </div>
      )}

      {phase === "error" && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4 space-y-2">
          <div className="flex items-start gap-2 text-red-400 text-sm">
            <XCircle size={15} className="flex-shrink-0 mt-0.5" />
            <span>{errorMsg}</span>
          </div>
          <button
            onClick={() => setPhase("idle")}
            className="text-xs text-zinc-500 hover:text-zinc-300 underline"
          >
            Erneut versuchen
          </button>
        </div>
      )}
    </div>
  );
}

// ── Live-Log ──────────────────────────────────────────────────────────────────
function LiveLog({ log, running }) {
  const bottomRef = useRef(null);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [log]);
  if (!log?.length && !running) return null;
  return (
    <div className="rounded-xl border border-zinc-700/50 bg-zinc-950 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-800 bg-zinc-900/50">
        <span className="text-xs text-zinc-400 font-medium flex items-center gap-2">
          {running && <Loader2 size={12} className="animate-spin text-amber-400" />}
          Live-Log
        </span>
      </div>
      <div className="p-4 font-mono text-xs text-zinc-300 max-h-48 overflow-y-auto space-y-1">
        {log.map((line, i) => (
          <div key={i} className={`leading-relaxed ${line.includes("FEHLER") ? "text-red-400" : line.includes("erfolgreich") ? "text-emerald-400" : ""}`}>
            {line}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

// ── Datei-Liste ───────────────────────────────────────────────────────────────
function FileList({ files, onDelete, onDownload }) {
  if (!files.length) {
    return <div className="text-center py-10 text-zinc-500 text-sm">Noch keine Backups vorhanden.</div>;
  }
  return (
    <div className="divide-y divide-zinc-800/50">
      {files.map(f => (
        <div key={f.id} className="flex items-center gap-3 py-3 px-1" data-testid={`backup-file-${f.id}`}>
          <FileArchive size={16} className="text-zinc-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white truncate font-mono">{f.name}</p>
            <p className="text-xs text-zinc-500">{f.size_mb} MB · {new Date(f.created).toLocaleString("de-DE")}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button data-testid={`download-${f.id}`} onClick={() => onDownload(f.id, f.name)} title="Herunterladen" className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-md transition-colors"><Download size={14} /></button>
            <button data-testid={`delete-${f.id}`} onClick={() => onDelete(f.id)} title="Löschen" className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-colors"><Trash2 size={14} /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Hauptseite ────────────────────────────────────────────────────────────────
export default function BackupAdminPage() {
  const { token } = useAuth();
  const [status, setStatus]           = useState(null);
  const [files, setFiles]             = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [triggering, setTriggering]   = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const pollStatusRef = useRef(null);

  const headers = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/backup/status`, { headers: headers() });
      if (res.ok) setStatus(await res.json());
    } catch {}
  }, [headers]);

  const fetchFiles = useCallback(async () => {
    setLoadingFiles(true);
    try {
      const res = await fetch(`${API}/api/backup/files`, { headers: headers() });
      if (res.ok) setFiles(await res.json());
    } catch {}
    setLoadingFiles(false);
  }, [headers]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  useEffect(() => {
    if (status?.running) {
      pollStatusRef.current = setInterval(fetchStatus, 3000);
    } else {
      clearInterval(pollStatusRef.current);
      if (status?.connected) fetchFiles();
    }
    return () => clearInterval(pollStatusRef.current);
  }, [status?.running, status?.connected, fetchStatus, fetchFiles]);

  useEffect(() => { if (status?.connected) fetchFiles(); }, [status?.connected, fetchFiles]);

  const triggerBackup = async () => {
    setTriggering(true);
    try {
      const res = await fetch(`${API}/api/backup/trigger`, { method: "POST", headers: headers() });
      if (!res.ok) { let d; try { d = await res.json(); } catch {} alert(d?.detail || "Fehler"); }
      else await fetchStatus();
    } catch (e) { alert(e.message); }
    setTriggering(false);
  };

  const disconnect = async () => {
    if (!window.confirm("OneDrive-Verbindung wirklich trennen?")) return;
    setDisconnecting(true);
    await fetch(`${API}/api/backup/disconnect`, { method: "DELETE", headers: headers() });
    await fetchStatus();
    setFiles([]);
    setDisconnecting(false);
  };

  const handleDelete = async (id) => {
    setDeleteConfirm(null);
    await fetch(`${API}/api/backup/files/${id}`, { method: "DELETE", headers: headers() });
    fetchFiles();
  };

  const handleDownload = async (id, name) => {
    try {
      const res = await fetch(`${API}/api/backup/files/${id}/download`, { headers: headers() });
      const data = await res.json();
      if (data.url) { const a = document.createElement("a"); a.href = data.url; a.download = name; a.click(); }
    } catch {}
  };

  const connected    = status?.connected;
  const tokenStored  = status?.token_stored;
  const needsReauth  = tokenStored && !connected;

  return (
    <AdminLayout>
      <div className="p-8 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-white font-heading flex items-center gap-2">
              <HardDrive size={22} className="text-rose-400" />
              Datenbank-Backup
            </h1>
            <p className="text-zinc-400 text-sm mt-1">
              Automatisches tägliches Backup nach OneDrive · 14-Tage Aufbewahrung
            </p>
          </div>
          {connected && (
            <button data-testid="disconnect-btn" onClick={disconnect} disabled={disconnecting}
              className="flex items-center gap-2 px-3 py-2 text-xs text-zinc-400 hover:text-red-400 hover:bg-red-500/10 border border-zinc-700/50 rounded-lg transition-colors">
              {disconnecting ? <Loader2 size={13} className="animate-spin" /> : <Unplug size={13} />} Trennen
            </button>
          )}
        </div>

        {status && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard icon={connected ? <CheckCircle2 size={15} className="text-emerald-400" /> : <XCircle size={15} className="text-zinc-500" />} label="OneDrive" value={connected ? (status.onedrive_user || "Verbunden") : "Nicht verbunden"} small={connected} />
            <StatCard icon={<Clock size={15} className="text-zinc-400" />} label="Letztes Backup" value={status.last_backup ? new Date(status.last_backup).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "–"} />
            <StatCard icon={status.last_status === "success" ? <CheckCircle2 size={15} className="text-emerald-400" /> : status.last_status === "error" ? <XCircle size={15} className="text-red-400" /> : <Info size={15} className="text-zinc-500" />} label="Status" value={status.last_status || "–"} />
            <StatCard icon={<FileArchive size={15} className="text-zinc-400" />} label="Größe" value={status.last_size_mb ? `${status.last_size_mb} MB` : "–"} />
          </div>
        )}

        <div className="bg-zinc-900 border border-white/5 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-white/5">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              {needsReauth
                ? <><AlertTriangle size={14} className="text-amber-400" /> Token abgelaufen – Neu verbinden</>
                : connected ? "OneDrive verbunden" : "OneDrive einrichten"}
            </h2>
          </div>
          <div className="p-5 space-y-5">
            {needsReauth && (
              <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-500/5 border border-amber-500/20">
                <AlertTriangle size={15} className="text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-zinc-300">
                  <p className="font-semibold text-amber-300 mb-1">OneDrive-Token ist abgelaufen</p>
                  <p className="text-zinc-400">Microsoft-Tokens laufen nach 14 Tagen Inaktivität ab. Bitte einmalig neu verbinden – deine Azure Client-ID ist bereits vorausgefüllt.</p>
                </div>
              </div>
            )}
            {(!connected) ? (
              <SetupWizard
                token={token}
                onConnected={async () => { await fetchStatus(); }}
                prefillClientId={status?.client_id || ""}
              />
            ) : (
              <>
                {/* Speicherort-Info */}
                <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-zinc-800/60 border border-white/[0.05]">
                  <FolderOpen size={15} className="text-sky-400 flex-shrink-0 mt-0.5" />
                  <div className="text-xs">
                    <p className="text-white font-semibold mb-0.5">OneDrive-Speicherort</p>
                    <p className="text-zinc-400">
                      Backups werden automatisch im Ordner{" "}
                      <code className="bg-zinc-700 px-1.5 py-0.5 rounded text-sky-300 font-mono">HUWND-Backups</code>{" "}
                      gespeichert. Dieser Ordner wird beim ersten Backup automatisch angelegt – du musst ihn nicht manuell erstellen.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <button data-testid="trigger-backup-btn" onClick={triggerBackup} disabled={status?.running || triggering}
                    className="flex items-center gap-2 px-5 py-2.5 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors">
                    {(status?.running || triggering) ? <><Loader2 size={15} className="animate-spin" /> Backup läuft…</> : <><Play size={15} /> Backup jetzt starten</>}
                  </button>
                  <span className="text-xs text-zinc-500 flex items-center gap-1.5"><Clock size={12} /> Automatisch täglich um 03:00 Uhr</span>
                </div>
                {(status?.running || status?.log?.length > 0) && <LiveLog log={status.log} running={status.running} />}
                {status?.last_status === "error" && status?.last_error && (
                  <div className="flex items-start gap-2 text-sm text-red-400 bg-red-500/5 border border-red-500/20 rounded-lg p-3">
                    <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" />
                    <span>{status.last_error}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {connected && (
          <div className="bg-zinc-900 border border-white/5 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
              <h2 className="text-sm font-semibold text-white">Gespeicherte Backups{files.length > 0 && <span className="ml-2 text-xs text-zinc-500 font-normal">({files.length})</span>}</h2>
              <button data-testid="refresh-files-btn" onClick={fetchFiles} disabled={loadingFiles} className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-md transition-colors">
                <RefreshCw size={14} className={loadingFiles ? "animate-spin" : ""} />
              </button>
            </div>
            <div className="px-5 py-2">
              {loadingFiles && !files.length
                ? <div className="flex items-center gap-2 py-8 justify-center text-zinc-500 text-sm"><Loader2 size={16} className="animate-spin" /> Lade Dateien…</div>
                : <FileList files={files} onDelete={id => setDeleteConfirm(id)} onDownload={handleDownload} />
              }
            </div>
          </div>
        )}
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-zinc-900 border border-white/5 rounded-xl p-6 w-full max-w-sm mx-4 space-y-4">
            <h3 className="text-white font-semibold flex items-center gap-2"><Trash2 size={16} className="text-red-400" /> Backup löschen?</h3>
            <p className="text-zinc-400 text-sm">Diese Datei wird dauerhaft von OneDrive gelöscht.</p>
            <div className="flex gap-3">
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 py-2 bg-red-600 hover:bg-red-500 text-white text-sm font-semibold rounded-lg transition-colors" data-testid="confirm-delete-btn">Löschen</button>
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm rounded-lg transition-colors">Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function StatCard({ icon, label, value, small }) {
  return (
    <div className="bg-zinc-900 border border-white/5 rounded-xl p-3">
      <div className="flex items-center gap-1.5 text-zinc-500 text-xs mb-1.5">{icon} {label}</div>
      <p className={`font-semibold text-white ${small ? "text-xs truncate" : "text-sm"}`}>{value}</p>
    </div>
  );
}
